(function installMonogramFeeGuard(win, doc) {
  if (win.EtoilePersonalisationFeeGuard) return;
  win.EtoilePersonalisationFeeGuard = true;
  const config = win.EtoileMonogramFeeGuardConfig || {};
  const feeHandle = String(config.feeProductHandle || "").trim();
  const root = String(win.Shopify?.routes?.root || "/");
  const localeRoot = `/${root.replace(/^\/+|\/+$/g, "")}/`.replace("//", "/");
  const originalFetch = win.fetch.bind(win);
  const pendingIds = /* @__PURE__ */ new Map();
  const pendingAttempts = /* @__PURE__ */ new Map();
  const approvedCheckoutSubmits = /* @__PURE__ */ new WeakMap();
  let pendingMutations = Promise.resolve();
  let feeProductPromise = null;
  let knownPersonalizedCart = false;
  let issue = "";
  let lastReport = null;
  let banner = null;
  let repairInProgress = false;
  function enqueueMutation(work) {
    const operation = pendingMutations.then(work, work);
    pendingMutations = operation.then(() => void 0, () => void 0);
    return operation;
  }
  const normalizeId = (value) => String(value ?? "").replace(/^gid:\/\/shopify\/ProductVariant\//, "");
  const isCartUrl = (value, endpoint) => {
    try {
      return new URL(String(value), win.location.href).pathname.endsWith(endpoint);
    } catch {
      return String(value || "").includes(endpoint);
    }
  };
  const isPersonalized = (item) => {
    const properties = item?.properties || {};
    if (String(properties._bundle_monogram_charge) === "true") return false;
    return Object.entries(properties).some(
      ([key, value]) => key.toLowerCase() === "personalisation" && String(value || "").trim().toLowerCase() === "monogram"
    );
  };
  const pairId = (item) => String(item?.properties?._monogram_id || item?.properties?._etoile_monogram_id || "");
  const itemId = (item) => normalizeId(item?.id ?? item?.variant_id);
  const itemQuantity = (item) => {
    const value = Number(item?.quantity);
    return Number.isFinite(value) && value > 0 ? value : 0;
  };
  const hasPersonalizedItems = (cart) => Array.isArray(cart?.items) && cart.items.some(isPersonalized);
  const createPairId = () => {
    if (win.crypto && typeof win.crypto.randomUUID === "function") {
      return win.crypto.randomUUID();
    }
    if (win.crypto && typeof win.crypto.getRandomValues === "function") {
      const bytes = new Uint8Array(16);
      win.crypto.getRandomValues(bytes);
      return Array.from(bytes, (value) => value.toString(16).padStart(2, "0")).join("");
    }
    throw new Error("This browser cannot safely identify a personalisation fee pair.");
  };
  function present(message, report) {
    issue = message;
    lastReport = report || lastReport;
    if (!doc.body) {
      doc.addEventListener("DOMContentLoaded", () => present(message, report), { once: true });
      return;
    }
    if (!banner) {
      banner = doc.createElement("div");
      banner.setAttribute("data-etoile-fee-guard", "");
      Object.assign(banner.style, {
        position: "fixed",
        left: "12px",
        right: "12px",
        bottom: "12px",
        zIndex: "2147483647",
        padding: "14px 16px",
        color: "#3b1e12",
        background: "#fff4e5",
        border: "1px solid #9a4b00",
        boxShadow: "0 4px 18px rgba(0,0,0,.18)",
        font: "14px/1.45 Arial,sans-serif"
      });
      doc.body.appendChild(banner);
    }
    banner.textContent = message + " ";
    const cartLink = doc.createElement("a");
    cartLink.href = `${localeRoot}cart`;
    cartLink.textContent = "Return to cart";
    cartLink.style.marginRight = "12px";
    banner.appendChild(cartLink);
    if (report?.missing?.length) {
      const repair = doc.createElement("button");
      repair.type = "button";
      repair.textContent = "Add missing fee";
      repair.disabled = repairInProgress;
      repair.addEventListener("click", () => {
        if (repairInProgress) return;
        repairInProgress = true;
        repair.disabled = true;
        enqueueMutation(() => repairMissingFees()).finally(() => {
          repairInProgress = false;
          if (repair.isConnected) repair.disabled = false;
        });
      });
      banner.appendChild(repair);
    } else {
      const recheck = doc.createElement("button");
      recheck.type = "button";
      recheck.textContent = "Recheck cart";
      recheck.addEventListener("click", () => auditCart());
      banner.appendChild(recheck);
    }
  }
  function clearIssue() {
    issue = "";
    lastReport = null;
    if (banner) {
      banner.remove();
      banner = null;
    }
  }
  async function getFeeProduct() {
    if (!feeHandle) throw new Error("The personalisation fee product is not configured.");
    if (!feeProductPromise) {
      const url = `${localeRoot}products/${encodeURIComponent(feeHandle)}.js`;
      feeProductPromise = originalFetch(url, {
        method: "GET",
        credentials: "same-origin",
        cache: "no-store",
        headers: { Accept: "application/json" }
      }).then(async (response) => {
        if (!response.ok) throw new Error("The configured personalisation fee is unavailable.");
        const product = await response.json();
        const variants = product.variants || [];
        return {
          variants: variants.map((item) => ({
            id: normalizeId(item.id),
            available: item.available === true,
            price: Number(item.price)
          })),
          variantIds: variants.map((item) => normalizeId(item.id))
        };
      }).catch((error) => {
        feeProductPromise = null;
        throw error;
      });
    }
    return feeProductPromise;
  }
  function resolveFeeVariant(fee, explicitVariantId) {
    const eligible = fee.variants.filter(
      (variant) => variant.available && variant.price > 0
    );
    if (explicitVariantId) {
      const explicit = fee.variants.find((variant) => variant.id === explicitVariantId);
      if (!explicit) {
        throw new Error("The native personalisation fee variant is not part of the configured fee product. Correct the fee product configuration before retrying.");
      }
      if (explicit.available && explicit.price > 0) {
        return { ...fee, variantId: explicit.id, price: explicit.price };
      }
    }
    if (eligible.length !== 1) {
      throw new Error(eligible.length ? "The configured personalisation fee has multiple eligible variants. Select one fee variant in the monogram setup." : "The configured personalisation fee has no available, priced variant.");
    }
    return { ...fee, variantId: eligible[0].id, price: eligible[0].price };
  }
  function feeLineHasPositiveCharge(item) {
    const quantity = itemQuantity(item);
    if (!quantity) return false;
    const lineTotal = item?.final_line_price;
    if (lineTotal !== null && lineTotal !== void 0 && lineTotal !== "") {
      const value = Number(lineTotal);
      return Number.isFinite(value) && value > 0;
    }
    const finalPrice = Number(item?.final_price);
    return Number.isFinite(finalPrice) && finalPrice * quantity > 0;
  }
  function feeUnits(item) {
    const p = item?.properties || {};
    if (!p._bundle_group || !p._bundle_monogram_variants) return 1;
    try {
      const variants = JSON.parse(p._bundle_monogram_variants);
      if (Array.isArray(variants) && variants.length > 0 && variants.every(id => /^\d+$/.test(String(id)))) return variants.length;
    } catch {}
    throw new Error("The bundle monogram component references are invalid.");
  }
  function checkCart(cart, feeVariantIds) {
    const items = Array.isArray(cart?.items) ? cart.items : [];
    const personalized = items.filter(isPersonalized);
    const fees = items.filter((item) => feeVariantIds.includes(itemId(item)));
    const missing = [];
    const problems = [];
    const parentQuantities = /* @__PURE__ */ new Map();
    for (const item of personalized) {
      const id = pairId(item);
      if (!id) {
        problems.push("A personalised item is missing its fee-pair reference.");
        continue;
      }
      const quantity = itemQuantity(item) * feeUnits(item);
      if (!quantity) {
        problems.push("A personalised item has an invalid quantity.");
      } else {
        parentQuantities.set(id, (parentQuantities.get(id) || 0) + quantity);
      }
    }
    for (const [id, quantity] of parentQuantities) {
      const feeQuantity = fees.filter((fee) => pairId(fee) === id).reduce((sum, fee) => sum + itemQuantity(fee), 0);
      if (feeQuantity < quantity) {
        missing.push({ id, quantity: quantity - feeQuantity });
        problems.push("A personalised item is missing its matching fee quantity.");
      } else if (feeQuantity > quantity) {
        problems.push("A personalisation fee quantity exceeds its matching item quantity.");
      }
    }
    for (const fee of fees) {
      const id = pairId(fee);
      if (!id) {
        problems.push("A personalisation fee is missing its fee-pair reference.");
      } else if (!parentQuantities.has(id)) {
        problems.push("A personalisation fee no longer has its matching item.");
      }
      if (!feeLineHasPositiveCharge(fee)) {
        problems.push("A personalisation fee has no positive final charge after discounts.");
      }
    }
    return { valid: problems.length === 0, problems, missing, personalizedCount: personalized.length };
  }
  function pairQuantity(cart, id, feeVariantIds, isFee) {
    return (Array.isArray(cart?.items) ? cart.items : []).filter(
      (item) => pairId(item) === id && (isFee ? feeVariantIds.includes(itemId(item)) : isPersonalized(item))
    ).reduce((sum, item) => sum + itemQuantity(item), 0);
  }
  function captureAddAttempt(cart, expected, feeVariantIds) {
    return {
      feeVariantIds,
      expected: expected.map((item) => {
        const id = pairId(item);
        return {
          id,
          quantity: itemQuantity(item),
          requiredFeeQuantity: itemQuantity(item) * feeUnits(item),
          productQuantity: pairQuantity(cart, id, feeVariantIds, false),
          feeQuantity: pairQuantity(cart, id, feeVariantIds, true)
        };
      })
    };
  }
  function reconcileAddAttempt(cart, attempt) {
    let unchanged = true;
    let fulfilled = true;
    for (const expected of attempt.expected) {
      const productDelta = pairQuantity(cart, expected.id, attempt.feeVariantIds, false) - expected.productQuantity;
      const feeDelta = pairQuantity(cart, expected.id, attempt.feeVariantIds, true) - expected.feeQuantity;
      if (productDelta !== 0 || feeDelta !== 0) unchanged = false;
      if (productDelta < expected.quantity || feeDelta < expected.requiredFeeQuantity) fulfilled = false;
    }
    if (fulfilled && checkCart(cart, attempt.feeVariantIds).valid) return "fulfilled";
    if (unchanged) return "unchanged";
    return "partial";
  }
  async function readCart() {
    const response = await originalFetch(`${localeRoot}cart.js`, {
      method: "GET",
      credentials: "same-origin",
      cache: "no-store",
      headers: { Accept: "application/json" }
    });
    if (!response.ok) throw new Error(`Shopify cart could not be checked (HTTP ${response.status}).`);
    return response.json();
  }
  async function auditCart() {
    try {
      const cart = await readCart();
      if (!hasPersonalizedItems(cart) && !knownPersonalizedCart) {
        clearIssue();
        return { valid: true, problems: [], missing: [], personalizedCount: 0 };
      }
      const fee = await getFeeProduct();
      const report = checkCart(cart, fee.variantIds);
      knownPersonalizedCart = report.personalizedCount > 0 || knownPersonalizedCart;
      if (report.valid) clearIssue();
      else present(report.problems.join(" "), report);
      return report;
    } catch (error) {
      if (knownPersonalizedCart) {
        present(error instanceof Error ? error.message : "The personalisation fee could not be verified.");
      }
      throw error;
    }
  }
  async function repairMissingFees() {
    try {
      const cart = await readCart();
      const feeProduct = await getFeeProduct();
      const cartFeeVariantIds = Array.from(new Set((cart.items || []).map(itemId).filter((id) => feeProduct.variantIds.includes(id))));
      const fee = cartFeeVariantIds.length === 1 ? resolveFeeVariant(feeProduct, cartFeeVariantIds[0]) : resolveFeeVariant(feeProduct);
      const report = checkCart(cart, fee.variantIds);
      if (report.problems.some(
        (problem) => problem !== "A personalised item is missing its matching fee quantity."
      )) {
        present(report.problems.join(" "), report);
        return;
      }
      if (!report.missing.length) {
        if (report.valid) clearIssue();
        else present(report.problems.join(" "), report);
        return;
      }
      const items = report.missing.map(({ id, quantity }) => ({
        id: fee.variantId,
        quantity,
        properties: { _monogram_id: id }
      }));
      const response = await originalFetch(`${localeRoot}cart/add.js`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ items })
      });
      if (!response.ok) throw new Error("Shopify did not confirm the fee repair. Recheck the cart before trying again.");
      const freshCart = await readCart();
      const freshReport = checkCart(freshCart, fee.variantIds);
      if (!freshReport.valid) {
        present(`The fee repair could not be verified. ${freshReport.problems.join(" ")}`, freshReport);
        return;
      }
      clearIssue();
    } catch (error) {
      present(error instanceof Error ? error.message : "The personalisation fee could not be repaired.");
    }
  }
  function parseFormEntries(entries) {
    const rootPayload = {};
    const items = /* @__PURE__ */ new Map();
    for (const [key, value] of entries) {
      if (typeof value !== "string") throw new Error("This personalisation form contains an unsupported file field.");
      let match = key.match(/^items\[(\d+)\]\[(.+)\]$/);
      if (match) {
        const index2 = Number(match[1]);
        const item = items.get(index2) || { properties: {} };
        const rest = match[2];
        const property2 = rest.match(/^properties\]\[(.+)$/) || rest.match(/^properties\[(.+)\]$/);
        if (property2) item.properties[property2[1]] = value;
        else item[rest] = value;
        items.set(index2, item);
        continue;
      }
      const property = key.match(/^properties\[(.+)\]$/);
      if (property) {
        rootPayload.properties = rootPayload.properties || {};
        rootPayload.properties[property[1]] = value;
      } else {
        rootPayload[key] = value;
      }
    }
    if (items.size) rootPayload.items = [...items.entries()].sort((a, b) => a[0] - b[0]).map(([, item]) => item);
    else if (rootPayload.id) {
      rootPayload.items = [{
        id: rootPayload.id,
        quantity: rootPayload.quantity || 1,
        properties: rootPayload.properties || {}
      }];
      delete rootPayload.id;
      delete rootPayload.quantity;
      delete rootPayload.properties;
    }
    return rootPayload;
  }
  function decodeBody(body) {
    if (typeof body === "string") {
      try {
        return { payload: JSON.parse(body), encoding: "json" };
      } catch {
        return { payload: parseFormEntries(new URLSearchParams(body).entries()), encoding: "form" };
      }
    }
    if (typeof URLSearchParams !== "undefined" && body instanceof URLSearchParams) {
      return { payload: parseFormEntries(body.entries()), encoding: "form" };
    }
    if (typeof FormData !== "undefined" && body instanceof FormData) {
      return { payload: parseFormEntries(body.entries()), encoding: "form" };
    }
    return null;
  }
  function stableIds(items) {
    const fingerprint = JSON.stringify(items.map((item) => ({
      id: item.id,
      quantity: item.quantity,
      properties: Object.fromEntries(Object.entries(item.properties || {}).filter(([key]) => key !== "_monogram_id"))
    })));
    let ids = pendingIds.get(fingerprint);
    if (!ids || ids.length !== items.length) {
      const used = /* @__PURE__ */ new Set();
      ids = items.map((item) => {
        let id = pairId(item);
        if (!id || used.has(id)) id = createPairId();
        used.add(id);
        return id;
      });
      pendingIds.set(fingerprint, ids);
    }
    return { ids, fingerprint };
  }
  async function preparePersonalizedPayload(items) {
    const personalized = items.filter(isPersonalized);
    if (!personalized.length) return null;
    if (personalized.some((item) => !itemId(item) || !itemQuantity(item))) {
      throw new Error("The personalised item has an invalid variant or quantity. It was not added.");
    }
    const feeProduct = await getFeeProduct();
    const configuredFeeItems = items.filter(
      (item) => feeProduct.variantIds.includes(itemId(item))
    );
    const configuredIds = new Set(configuredFeeItems.map((item) => itemId(item)));
    if (configuredIds.size > 1) {
      throw new Error("The personalised add contains multiple configured fee variants. Keep only the intended native fee item and retry.");
    }
    let explicitFeeVariantId = configuredIds.values().next().value;
    const nativeFeeFirstShape = items.length === 2 && isPersonalized(items[1]) && !isPersonalized(items[0]);
    if (!explicitFeeVariantId && nativeFeeFirstShape) {
      explicitFeeVariantId = itemId(items[0]);
    }
    const fee = resolveFeeVariant(feeProduct, explicitFeeVariantId);
    const { ids, fingerprint } = stableIds(personalized);
    const nextPersonalized = personalized.map((item, index2) => ({
      ...item,
      properties: { ...item.properties || {}, _monogram_id: ids[index2] }
    }));
    return {
      fee,
      _etoilePersonalizedItems: nextPersonalized,
      _etoileAttemptKey: fingerprint
    };
  }
  async function addBody(payload) {
    const sourceItems = Array.isArray(payload.items) ? payload.items : [{ id: payload.id, quantity: payload.quantity || 1, properties: payload.properties || {} }];
    const prepared = await preparePersonalizedPayload(sourceItems);
    if (!prepared) return null;
    const personalized = prepared._etoilePersonalizedItems;
    let personalizedIndex = 0;
    const nextItems = sourceItems.map((item) => {
      const parent = isPersonalized(item) ? personalized[personalizedIndex++] : null;
      return parent || item;
    });
    const nonFeeItems = nextItems.filter(
      (item) => !prepared.fee.variantIds.includes(itemId(item))
    );
    const feeItems = personalized.flatMap((item) => {
      const group = item.properties?._bundle_group;
      if (group && item.properties?._bundle_monogram_variants) {
        const charges = sourceItems.filter(line => prepared.fee.variantIds.includes(itemId(line)) &&
          line.properties?._bundle_group === group && String(line.properties?._bundle_monogram_charge) === "true");
        if (charges.length !== feeUnits(item) || charges.some(line => itemQuantity(line) !== itemQuantity(item) || !line.properties?.SKU || !line.properties?.Letters)) {
          throw new Error("The bundle monogram fees do not match its personalised components. Please reselect the monograms.");
        }
        return charges.map(line => ({...line, id: prepared.fee.variantId,
          properties: {...line.properties, _monogram_id: pairId(item)}}));
      }
      return [{id: prepared.fee.variantId, quantity: itemQuantity(item), properties: {_monogram_id: pairId(item)}}];
    });
    const output = { ...payload, items: [...nonFeeItems, ...feeItems] };
    delete output.id;
    delete output.quantity;
    delete output.properties;
    return {
      output,
      expected: personalized,
      fee: prepared.fee,
      attemptKey: prepared._etoileAttemptKey
    };
  }
  function addFailure(message) {
    present(message);
  }
  async function verifyAfterAdd(response, expected, fee, previousCart) {
    try {
      if (!response.ok) {
        const message = "Shopify did not confirm the personalised add. The cart may have changed partially; review it before trying again.";
        addFailure(message);
        return { ok: false, message };
      }
      const cart = await readCart();
      const report = checkCart(cart, fee.variantIds);
      const expectedValid = expected.every((parent) => {
        const id = pairId(parent);
        const quantityFor = (items, isFee) => (items || []).filter(
          (item) => pairId(item) === id && (isFee ? fee.variantIds.includes(itemId(item)) : isPersonalized(item))
        ).reduce((sum, item) => sum + itemQuantity(item), 0);
        const previousProductQuantity = quantityFor(previousCart.items, false);
        const previousFeeQuantity = quantityFor(previousCart.items, true);
        return quantityFor(cart.items, false) >= previousProductQuantity + itemQuantity(parent) && quantityFor(cart.items, true) >= previousFeeQuantity + itemQuantity(parent) * feeUnits(parent);
      });
      if (!expectedValid || !report.valid) {
        const message = `The personalised add was not fully verified in your cart. ${report.problems.join(" ")}`.trim();
        present(message, report);
        return { ok: false, message };
      }
      knownPersonalizedCart = true;
      clearIssue();
      return { ok: true };
    } catch (error) {
      const message = error instanceof Error ? `The personalised add may have reached your cart, but could not be verified. ${error.message} Review the cart before trying again.` : "The personalised add could not be verified. Review the cart before trying again.";
      present(message);
      return { ok: false, message };
    }
  }
  function shopifyErrorResponse(message) {
    return new Response(JSON.stringify({
      status: 422,
      message: "Personalisation fee could not be confirmed",
      description: message
    }), {
      status: 422,
      headers: { "Content-Type": "application/json" }
    });
  }
  function requestHeaders(init, input) {
    const headers = new Headers(init?.headers || input?.headers || {});
    headers.set("Content-Type", "application/json");
    headers.set("Accept", headers.get("Accept") || "application/json");
    return headers;
  }
  async function handleFetchAdd(input, init) {
    let upstreamAttempted = false;
    try {
      let body = init?.body;
      let requestInput = input;
      if (typeof Request !== "undefined" && input instanceof Request && body === void 0) {
        const contentType = input.headers.get("content-type") || "";
        body = contentType.includes("application/json") ? await input.clone().text() : await input.clone().formData();
      }
      const decoded = decodeBody(body);
      if (!decoded) {
        const message = "This cart add could not be safely inspected and was blocked. Use the standard storefront add control.";
        addFailure(message);
        return shopifyErrorResponse(message);
      }
      const prepared = await addBody(decoded.payload);
      if (!prepared) return originalFetch(input, init);
      const previousCart = await readCart();
      const pendingAttempt = pendingAttempts.get(prepared.attemptKey);
      if (pendingAttempt) {
        const state = reconcileAddAttempt(previousCart, pendingAttempt);
        if (state === "fulfilled") {
          pendingAttempts.delete(prepared.attemptKey);
          knownPersonalizedCart = true;
          clearIssue();
          return new Response(JSON.stringify({ items: prepared.output.items }), {
            status: 200,
            headers: { "Content-Type": "application/json" }
          });
        }
        if (state === "partial") {
          const report = checkCart(previousCart, pendingAttempt.feeVariantIds);
          const message = "A previous personalised add changed the cart only partially or its result is uncertain. No retry was sent. Review the cart before trying a new add.";
          present(message, report);
          return shopifyErrorResponse(message);
        }
        pendingAttempts.delete(prepared.attemptKey);
      }
      const previousReport = checkCart(previousCart, prepared.fee.variantIds);
      if (!previousReport.valid) {
        const message = `Resolve the existing personalisation fee issue before adding another personalised item. ${previousReport.problems.join(" ")}`;
        present(message, previousReport);
        return shopifyErrorResponse(message);
      }
      const attempt = captureAddAttempt(
        previousCart,
        prepared.expected,
        prepared.fee.variantIds
      );
      pendingAttempts.set(prepared.attemptKey, attempt);
      knownPersonalizedCart = true;
      const fetchInit = Object.assign({}, init || {}, {
        method: "POST",
        headers: requestHeaders(init, typeof input === "string" ? null : input),
        body: JSON.stringify(prepared.output)
      });
      if (typeof Request !== "undefined" && input instanceof Request) {
        requestInput = new Request(input, fetchInit);
        delete fetchInit.body;
        delete fetchInit.headers;
        delete fetchInit.method;
      }
      upstreamAttempted = true;
      const response = await originalFetch(requestInput, typeof Request !== "undefined" && input instanceof Request ? void 0 : fetchInit);
      const verification = await verifyAfterAdd(
        response,
        prepared.expected,
        prepared.fee,
        previousCart
      );
      if (!verification?.ok) {
        return shopifyErrorResponse(verification?.message || "The fee could not be verified in the cart.");
      }
      pendingAttempts.delete(prepared.attemptKey);
      return response;
    } catch (error) {
      const detail = error instanceof Error ? error.message : "The personalisation fee could not be confirmed.";
      const message = upstreamAttempted ? `${detail} The personalised add may have reached your cart; review it before trying again.` : `${detail} The product was not added.`;
      addFailure(message);
      return shopifyErrorResponse(message);
    }
  }
  win.fetch = (input, init) => {
    const url = typeof input === "string" ? input : input?.url || "";
    const method = String(init?.method || input?.method || "GET").toUpperCase();
    const isAdd = method === "POST" && isCartUrl(url, "/cart/add.js");
    const isEdit = method === "POST" && (isCartUrl(url, "/cart/change.js") || isCartUrl(url, "/cart/update.js") || isCartUrl(url, "/cart/clear.js"));
    if (isAdd) {
      return enqueueMutation(async () => handleFetchAdd(input, init));
    }
    if (isEdit) {
      return enqueueMutation(async () => {
        const response = await originalFetch(input, init);
        if (response.ok) await auditCart().catch(() => {
        });
        return response;
      });
    }
    return originalFetch(input, init);
  };
  async function handlePersonalizedForm(form, explanation) {
    const FormDataConstructor = FormData;
    const data = new FormDataConstructor(form);
    const hasMarker = Array.from(data.entries()).some(
      ([key, value]) => key.toLowerCase() === "properties[personalisation]" && String(value).toLowerCase() === "monogram"
    );
    if (!hasMarker) data.append("properties[Personalisation]", "Monogram");
    try {
      const response = await win.fetch(`${localeRoot}cart/add.js`, {
        method: "POST",
        body: data,
        credentials: "same-origin"
      });
      if (!response.ok) return;
      if (explanation) {
        present("Buy now is disabled for personalised items so the fee can be verified. Continue from your cart when ready.");
      }
      win.location.assign(`${localeRoot}cart`);
    } catch {
    }
  }
  function formHasPersonalization(form) {
    if (isPlacementPreview()) return false;
    const FormDataConstructor = FormData;
    const values = Array.from(new FormDataConstructor(form).entries());
    return values.some(([key, value]) => {
      const normalizedKey = key.toLowerCase();
      if (normalizedKey === "properties[personalisation]") {
        return String(value).toLowerCase() === "monogram";
      }
      return /(?:letters?|initials?)/i.test(normalizedKey) && String(value || "").trim().length > 0;
    }) || monogramInputsForForm(form).some((input) => String(input.value || "").trim().length > 0);
  }
  function isPlacementPreview() {
    if (doc.querySelector?.(
      "[data-etoile-placement-review-preview], [data-etoile-placement-editor]"
    )) return true;
    try {
      const params = new URL(win.location.href).searchParams;
      return params.get("etoile_review_preview") === "1" || params.get("etoile_placement_editor") === "1";
    } catch {
      return false;
    }
  }
  function monogramInputsForForm(form) {
    if (isPlacementPreview()) return [];
    const formWrapper = form?.closest?.(".product__form__wrapper");
    if (!formWrapper) return [];
    const productRoot = formWrapper.parentNode;
    const widgets = Array.from(
      productRoot?.querySelectorAll?.(".product-monogram") || []
    );
    if (widgets.length !== 1) return [];
    const widget = widgets[0];
    const associatedFormWrapper = widget.parentNode?.querySelector?.(".product__form__wrapper");
    if (associatedFormWrapper !== formWrapper) return [];
    if (widget.closest?.(
      "[data-etoile-placement-review-preview], [data-etoile-placement-editor]"
    )) return [];
    return Array.from(widget.querySelectorAll?.("input.input-letter") || []);
  }
  doc.addEventListener("submit", (event) => {
    const form = event.target;
    const action = String(form?.action || form?.getAttribute?.("action") || "");
    if (isCartUrl(action, "/cart/add") && formHasPersonalization(form)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      handlePersonalizedForm(form, false);
    }
  }, true);
  doc.addEventListener("click", (event) => {
    const target = event.target?.closest?.(
      ".shopify-payment-button__button, .shopify-payment-button button"
    );
    if (!target) return;
    const form = target.form || target.closest?.("form") || doc.querySelector('form[action*="/cart/add"]');
    if (!form || !formHasPersonalization(form)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    present("Buy now is unavailable for personalised items. Use the standard Add to cart control so the personalisation fee can be verified.");
  }, true);
  function isCheckoutSubmitter(target) {
    return String(target?.name || target?.getAttribute?.("name") || "").toLowerCase() === "checkout";
  }
  function continueCheckout(target, submitter) {
    const isForm = target?.tagName?.toLowerCase() === "form";
    const form = isForm ? target : target?.form || submitter?.form;
    const formSubmitter = submitter?.form === form ? submitter : null;
    if (form) {
      approvedCheckoutSubmits.set(form, formSubmitter);
      if (typeof form.requestSubmit === "function") {
        try {
          if (formSubmitter) form.requestSubmit(formSubmitter);
          else form.requestSubmit();
          return;
        } catch {
          approvedCheckoutSubmits.delete(form);
        }
      }
      approvedCheckoutSubmits.delete(form);
      if (formSubmitter && isCheckoutSubmitter(formSubmitter)) {
        const intent = doc.createElement("input");
        intent.type = "hidden";
        intent.name = "checkout";
        intent.value = String(formSubmitter.value || "");
        form.appendChild(intent);
        form.submit();
        intent.remove?.();
      } else if (typeof form.submit === "function") {
        form.submit();
      }
      return;
    }
    const href = target?.href;
    if (href) win.location.assign(href);
    else win.location.assign(`${localeRoot}checkout`);
  }
  function gateCheckout(event, target, submitter) {
    event.preventDefault();
    event.stopImmediatePropagation();
    enqueueMutation(async () => {
      try {
        const cart = await readCart();
        if (!hasPersonalizedItems(cart) && !knownPersonalizedCart) {
          clearIssue();
          continueCheckout(target, submitter);
          return;
        }
        knownPersonalizedCart = knownPersonalizedCart || hasPersonalizedItems(cart);
        const fee = await getFeeProduct();
        const report = checkCart(cart, fee.variantIds);
        knownPersonalizedCart = report.personalizedCount > 0 || knownPersonalizedCart;
        if (!report.valid) {
          present(report.problems.join(" "), report);
          return;
        }
        clearIssue();
        continueCheckout(target, submitter);
      } catch (error) {
        if (knownPersonalizedCart) {
          present(error instanceof Error ? `${error.message} Checkout is paused until the personalisation fee can be verified.` : "Checkout is paused until the personalisation fee can be verified.");
          return;
        }
        continueCheckout(target, submitter);
      }
    });
  }
  doc.addEventListener("submit", (event) => {
    const form = event.target;
    const action = String(form?.action || form?.getAttribute?.("action") || "");
    const submitter = event.submitter || null;
    if (approvedCheckoutSubmits.has(form)) {
      const approvedSubmitter = approvedCheckoutSubmits.get(form);
      if (approvedSubmitter === submitter) {
        approvedCheckoutSubmits.delete(form);
        return;
      }
      approvedCheckoutSubmits.delete(form);
    }
    if (isCartUrl(action, "/checkout") || isCheckoutSubmitter(submitter)) {
      gateCheckout(event, form, submitter);
    }
  }, true);
  doc.addEventListener("click", (event) => {
    const target = event.target?.closest?.('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]');
    if (target) gateCheckout(event, target, target);
  }, true);
  const xhrPrototype = win.XMLHttpRequest?.prototype;
  if (xhrPrototype && !xhrPrototype.__etoileFeeGuard) {
    xhrPrototype.__etoileFeeGuard = true;
    const open = xhrPrototype.open;
    const send = xhrPrototype.send;
    const setRequestHeader = xhrPrototype.setRequestHeader;
    xhrPrototype.setRequestHeader = function(name, value) {
      const xhr = this;
      if (name.toLowerCase() === "content-type") {
        xhr.__etoileContentType = [name, value];
        return;
      }
      return setRequestHeader.apply(xhr, arguments);
    };
    xhrPrototype.open = function(method, url) {
      this.__etoileMethod = method;
      this.__etoileUrl = url;
      return open.apply(this, arguments);
    };
    xhrPrototype.send = function(body) {
      const xhr = this;
      const url = xhr.__etoileUrl || "";
      const method = String(xhr.__etoileMethod || "GET").toUpperCase();
      const isAdd = method === "POST" && isCartUrl(url, "/cart/add.js");
      const isEdit = method === "POST" && (isCartUrl(url, "/cart/change.js") || isCartUrl(url, "/cart/update.js") || isCartUrl(url, "/cart/clear.js"));
      const preserveContentType = () => {
        const header = xhr.__etoileContentType;
        delete xhr.__etoileContentType;
        if (header) setRequestHeader.apply(xhr, header);
      };
      if (!isAdd && !isEdit) {
        preserveContentType();
        return send.call(xhr, body);
      }
      const block = (message, report) => {
        present(message, report);
        try {
          xhr.abort();
        } catch {
        }
      };
      if (isAdd) {
        const decoded = decodeBody(body);
        if (!decoded?.payload) {
          block("This cart add could not be safely inspected and was blocked. Use the standard storefront add control.");
          return;
        }
        const sourceItems = Array.isArray(decoded.payload.items) ? decoded.payload.items : [decoded.payload];
        if (sourceItems.some(isPersonalized)) {
          block("Personalised XHR adds are blocked because their fee cannot be verified before the storefront reports success. Use the standard storefront add control.");
          return;
        }
      }
      enqueueMutation(async () => {
        if (isEdit) {
          try {
            const cart = await readCart();
            if (knownPersonalizedCart || hasPersonalizedItems(cart)) {
              knownPersonalizedCart = knownPersonalizedCart || hasPersonalizedItems(cart);
              block("This cart change was blocked because personalised cart edits cannot be safely verified through XHR. Use the cart page controls and recheck the fee.");
              return;
            }
          } catch {
            block("This cart change was blocked because the cart could not be checked first.");
            return;
          }
        }
        await new Promise((resolve, reject) => {
          let completed = false;
          const finish = () => {
            if (completed) return;
            completed = true;
            resolve();
          };
          xhr.addEventListener("loadend", finish, { once: true });
          try {
            preserveContentType();
            send.call(xhr, body);
          } catch (error) {
            finish();
            reject(error);
          }
        });
      }).catch((error) => {
        block(error instanceof Error ? `The cart request was blocked: ${error.message}` : "The cart request was blocked before it could be verified.");
      });
    };
  }
  doc.addEventListener("DOMContentLoaded", () => {
    auditCart().catch(() => {
    });
  }, { once: true });
  doc.addEventListener("cart:updated", () => auditCart().catch(() => {
  }));
  doc.addEventListener("cart:change", () => auditCart().catch(() => {
  }));
  doc.addEventListener("cart:refresh", () => auditCart().catch(() => {
  }));
})(window, document);

